import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Box, VStack, Text, Button, useToast, IconButton, Flex, Image, Collapse } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronLeft, FiChevronUp } from 'react-icons/fi';
import { collabApi } from '../../../services/api/collab';
import LocationSearchBox from '../scheduling/components/LocationSearchBox';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { createCustomToast } from '../components/CustomToast';
import { formatDateToYYYYMMDD, formatPostingDate } from 'utils/dateUtils';
import PostingDateSelector from '../components/PostingDateSelector';
import HelpSection from '../components/HelpSection';
import { useCampaignLocations } from 'hooks/useCampaignLocations';
import { Collab, isDeliveryOnlyUgc, resolveCollabProductValue } from "@foodfluence/shared";
import { submissionPathForCollab } from '../utils/collabRoutes';
import PostByConfirm, { getGoLiveConfirmation, goLiveDateRefusalMessage, usePostByConfirm } from '../components/PostByConfirm';
import type { ProductDefault } from '@foodfluence/shared';
import { needsProductSelection } from '../utils/productSelection';
import CollabConfirmedScreen from '../scheduling/components/CollabConfirmedScreen';
import ProductSelectionSummary from '../products/ProductSelectionSummary';
import { sumProductValues, type SelectedProduct } from '@foodfluence/shared';
import WizardSheet from '../scheduling/components/WizardSheet';
import { SheetButton, SheetFooter } from '../components/counter-flow/sheetParts';
import { eyebrowProps, stepTitleProps } from '../scheduling/styles';
import { ff } from '../../../theme';

const formatUsd = (usd?: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(usd) || 0);

const ScheduleRetailCollabPage: React.FC<{}> = (): JSX.Element => {
  const location = useLocation();
  const collabId = location.state?.collabId;
  // Carried from the product-selection step, when the campaign offers selection groups.
  const selectedProducts: ProductDefault[] | undefined = location.state?.selectedProducts;
  // Carried from the wizard's schedule step for a collab the brand accepted first. That collab has
  // neither field yet — the accept that writes them is the one this page is about to make — so
  // without these the creator re-enters what they just entered.
  const carriedLocation: string | undefined = location.state?.selectedLocation;
  const carriedPostingDate: string | undefined = location.state?.postingDate;
  const collabs = useContext(CollabDataContext);
  const collab = collabs.find(collab => collab.id === collabId);
  const [selectedDate, setSelectedDate] = useState<string | null>(
    () => collab?.scheduledPostingDate ?? carriedPostingDate ?? null
  );
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState<string>('');

  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  // Set once the accept lands: retail ends on the same confirmation the other flows do, rather
  // than dropping the creator back on the collab list with a toast.
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [frozenCollab, setFrozenCollab] = useState<Collab | null>(() => collab ? { ...collab } : null);
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const customToast = createCustomToast(toast);
  const clients = useContext(ClientDataContext);
  const campaigns = useContext(InfluencerCampaignDataContext);
  const campaign = campaigns[collab?.id];
  // Go-live campaigns: the window ticked on the review page, sent with the accept. A refusal shows
  // the checkbox here with the new dates, so the creator re-confirms without losing their picks.
  const carriedGoLive = collabId ? getGoLiveConfirmation(collabId, location.state) : undefined;
  const goLive = usePostByConfirm(collab?.status === 'pendingInfluencerApproval' ? campaign : null, collab, carriedGoLive);

  // Reachable without going through the selection wizard — the collab list and detail page both
  // link straight here for a collab the brand accepted first. The accept below would be refused
  // without a choice, so collect it before letting the creator schedule.
  const owesSelection = !selectedProducts?.length && needsProductSelection(collab, campaign);
  useEffect(() => {
    if (owesSelection && collab?.id) {
      navigate('/collabs/select-products', {
        state: { collabId: collab.id, destination: 'retail', goLiveConfirmed: carriedGoLive },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carriedGoLive is re-read each render
  }, [owesSelection, collab?.id, navigate]);
  const client = clients[campaign?.clientID];
  const influencerData = useContext(InfluencerDataContext);

  // Optimize: Only fetch proposed location IDs if they exist, otherwise fetch all campaign locations
  const { locations, loading: locationsLoading, error: locationsError } = useCampaignLocations(
    collab?.proposedSelectedLocationIds && collab.proposedSelectedLocationIds.length > 0 
      ? collab.proposedSelectedLocationIds 
      : campaign.foodfluenceLocations
  );
  

  const isPendingRedemption = collab?.status === 'pendingRedemption';

  useEffect(() => {
    if (isPendingRedemption) {
      // Pre-fill with existing values when in pendingRedemption status
      const postingDate = collab?.scheduledPostingDate ?? carriedPostingDate;
      if (postingDate) {
        setSelectedDate(postingDate);
      }
      const locationId = collab?.selectedLocation ?? carriedLocation;
      if (locationId && campaign?.foodfluenceLocations) {
        const location = locations.find(loc => loc.docId === locationId);
        if (location?.fullAddress) {
          setLocationSearchQuery(location.fullAddress);
        }
        setSelectedLocation(locationId);
      }
    } else {
      // Original logic for other statuses
      const locationId = collab?.selectedLocation ?? carriedLocation;
      if (locationId && campaign?.foodfluenceLocations) {
        const location = locations.find(loc => loc.docId === locationId);
        if (location?.fullAddress) {
          setLocationSearchQuery(location.fullAddress);
        }
      }
      setSelectedLocation(locationId);
    }
  }, [
    locations,
    collab?.selectedLocation,
    collab?.scheduledPostingDate,
    carriedLocation,
    carriedPostingDate,
    isPendingRedemption,
  ]);

  // Carried values RENDER but nothing writes them: the handlers below persist on change, and a
  // pre-filled field is never changed. The creator sees their location and date sitting there,
  // completes the collab, and the document has neither — which for retail is the pickup the brand
  // has to honour. Write them once on arrival, for a collab past the accept that would have.
  const carryPersisted = useRef(false);
  useEffect(() => {
    if (carryPersisted.current || !collab?.id) return;
    if (collab.status === 'pendingInfluencerApproval') return; // the accept below writes them
    const date = collab.scheduledPostingDate ?? carriedPostingDate;
    const locationId = collab.selectedLocation ?? carriedLocation;
    if (!date && !locationId) return;
    if (collab.scheduledPostingDate && collab.selectedLocation) return; // already stored
    carryPersisted.current = true;
    collabApi
      .updateRetailCollabPostingDate(collab.id, date, locationId)
      .catch((error) => console.error('Failed to persist carried schedule', error));
  }, [collab?.id, collab?.status, collab?.scheduledPostingDate, collab?.selectedLocation, carriedPostingDate, carriedLocation]);

  // Delivery-only UGC never goes on the creator's socials, so it is delivered, not posted.
  const deliveryOnly = isDeliveryOnlyUgc(collab, campaign);
  const dateTerm = deliveryOnly ? 'delivery date' : 'posting date';
  const postVerb = deliveryOnly ? 'deliver' : 'post';

  const savePostingDate = async (dateToSave: string) => {
    try {
      await collabApi.updateRetailCollabPostingDate(collab.id, dateToSave);
      customToast.showSuccessToast(
        'Date Saved',
        `Your estimated ${dateTerm} has been updated successfully.`
      );
    } catch (error) {
      console.error('Error saving posting date:', error);
      customToast.showErrorToast(
        'Error',
        goLiveDateRefusalMessage(error) ?? `Failed to save ${dateTerm}. Please try again.`
      );
      throw error;
    }
  };

  const saveLocation = async (locationId: string) => {
    try {
      await collabApi.updateRetailCollabPostingDate(collab.id, selectedDate, locationId);
      customToast.showSuccessToast(
        'Location Saved',
        'Your pickup location has been updated successfully.'
      );
    } catch (error) {
      console.error('Error saving location:', error);
      customToast.showErrorToast(
        'Error',
        'Failed to save pickup location. Please try again.'
      );
      throw error;
    }
  };

  const handleDateChange = async (date: Date) => {
    // Convert to YYYY-MM-DD using UTC to avoid timezone issues
    const newDate = formatDateToYYYYMMDD(date);
    setSelectedDate(newDate);

    // Only save immediately if collab is already accepted
    if (collab?.status !== 'pendingInfluencerApproval') {
      try {
        await savePostingDate(newDate);
      } catch (error) {
        // Error already handled by savePostingDate
        setSelectedDate(null); // Reset on error
      }
    }
  };

  const handleAcceptAndSchedule = async () => {
    if (!collab.id || !selectedDate) return;
    if (campaign?.foodfluenceLocations?.length > 0 && !selectedLocation) return;
    if (collab.status !== 'pendingInfluencerApproval') {
      customToast.showErrorToast(
        'Error',
        'This collab has already been accepted.'
      );
      return;
    }

    setIsRedeeming(true);
    try {
      await collabApi.acceptRetailCollab({
        collabId: collab.id,
        selectedLocation: selectedLocation,
        postingDate: selectedDate,
        ...(selectedProducts?.length ? { selectedProducts } : {}),
        goLiveConfirmed: goLive.goLiveConfirmed,
      });
      setIsConfirmed(true);
    } catch (error) {
      console.error('Error accepting collab:', error);
      if (goLive.handleError(error)) return;
      customToast.showErrorToast(
        'Error',
        goLiveDateRefusalMessage(error) ?? 'Failed to accept the collab. Please try again.'
      );
    } finally {
      setIsRedeeming(false);
      setIsConfirmModalOpen(false);
    }
  };

  const handleReadyToPost = async () => {
    if (!collab.id || !selectedDate) return;
    if (campaign?.foodfluenceLocations?.length > 0 && !selectedLocation) return;

    setIsRedeeming(true);
    try {      
      const requireDraftApproval = collab?.content && Object.keys(collab.content).length > 0 ? false : campaign?.requireDraftApproval;
      await collabApi.advanceCollabToContentSubmission({
        collabId: collab.id,
        skipRedeem: true,
        requireDraftApproval,
        // A collab the brand accepted first reaches this page at `pendingRedemption`, so this is
        // the only point at which the creator's choice — carried here by the selection wizard —
        // gets persisted. Without it the collab would settle on the campaign's estimate.
        ...(selectedProducts?.length ? { selectedProducts } : {})
      });
      
      customToast.showSuccessToast(
        'Collab Redeemed',
        `You have successfully redeemed this collab. ${deliveryOnly ? 'You can now upload your content.' : 'You can now proceed with posting.'}`
      );

      navigate(submissionPathForCollab(collab), { replace: true });
    } catch (error) {
      console.error('Error redeeming collab:', error);
      customToast.showErrorToast(
        'Error',
        'Failed to redeem the collab. Please try again.'
      );
    } finally {
      setIsRedeeming(false);
      setIsConfirmModalOpen(false);
    }
  };

  const handleMainButtonClick = () => {
    // Validate required fields
    if (!selectedDate && !selectedLocation) {
      customToast.showErrorToast(
        'Missing Date and Location',
        `Please select when you plan to ${postVerb} and a location to pick up from`
      );
      return;
    }
    if (!selectedDate) {
      customToast.showErrorToast(
        'Missing Date',
        `Please select when you plan to ${postVerb}`
      );
      return;
    }
    if (!selectedLocation) {
      customToast.showErrorToast(
        'Missing Location',
        'Please select a location to pick up from'
      );
      return;
    }

    // Freeze the collab state
    setFrozenCollab({ ...collab });
    setIsConfirmModalOpen(true);
  };

  const handleConfirmAction = () => {
    if (collab?.status === 'pendingInfluencerApproval') {
      handleAcceptAndSchedule();
    } else if (collab?.status === 'pendingRedemption') {
      handleReadyToPost();
    }
  };

  const getButtonText = () => {
    if (frozenCollab?.status === 'pendingInfluencerApproval') {
      return 'Accept and Schedule';
    } else if (frozenCollab?.status === 'pendingRedemption') {
      return 'I am Ready to Create Content';
    }
    return 'Accept and Schedule'; // fallback
  };

  const getConfirmationMessage = () => {
    const formattedDate = formatPostingDate(selectedDate);
    const locationText = selectedLocation ? ` at ${locations.find(loc => loc.docId === selectedLocation)?.fullAddress}` : '';

    if (frozenCollab?.status === 'pendingInfluencerApproval') {
      return `Accept this collab and schedule your ${dateTerm} for: ${formattedDate}${locationText}?`;
    } else if (frozenCollab?.status === 'pendingRedemption') {
      return `Make sure you have purchased your product and sent the receipt to the brand via the inbox. Once your content is verified, your reimbursement will be available in the wallet tab.`;
    }
    return `Schedule your ${dateTerm} for: ${formattedDate}${locationText}?`;
  };

  const handleLocationSelect = async (location: { docId: string; fullAddress: string }) => {
    setSelectedLocation(location.docId);
    setLocationSearchQuery(location.fullAddress);

    // Only save immediately if collab is already accepted
    if (collab?.status !== 'pendingInfluencerApproval') {
      try {
        await saveLocation(location.docId);
      } catch (error) {
        // Error already handled by saveLocation
        setSelectedLocation(null); // Reset on error
        setLocationSearchQuery('');
      }
    }
  };

  // Confirmation — full takeover, no page chrome, the same screen the other accept flows end on.
  if (isConfirmed) {
    const picks = (selectedProducts ?? []) as SelectedProduct[];
    return (
      <Flex direction="column" minH="100svh" bg="#C6F432">
        <Box w="100%" flex={1}>
          <CollabConfirmedScreen
            title="Collab confirmed"
            brandName={client?.accountName ?? 'Your brand'}
            brandLogoUrl={client?.instagramProfilePicture}
            creatorAvatarUrl={influencerData?.influencerData?.profile_pic_url}
            connectorLabel={picks.length ? 'Your Picks' : 'Confirmed'}
            subtext={
              selectedDate
                ? `Your collaboration is confirmed. You're ${deliveryOnly ? 'delivering' : 'posting'} on ${formatPostingDate(selectedDate)}.`
                : `Your collaboration is confirmed. You can ${postVerb} whenever you are ready.`
            }
            onViewCollab={() =>
              navigate(`/collabs/collab-details/${collab.id}`, { replace: true })
            }
            onHome={() => navigate('/')}
          >
            {picks.length > 0 && (
              <ProductSelectionSummary
                products={picks}
                totalUsd={sumProductValues(picks)}
                formatValue={formatUsd}
              />
            )}
          </CollabConfirmedScreen>
        </Box>
      </Flex>
    );
  }

  // Context card figures (board): only what the creator actually gets — cash, and the retail
  // product value. Retail never carries credit, and a $0 line is never shown.
  const cash = Math.round(collab?.cash ?? 0);
  const productValue = Math.round(campaign ? resolveCollabProductValue(collab, campaign) : 0);
  const figures = [
    ...(cash > 0 ? [{ label: 'Cash', value: cash }] : []),
    ...(productValue > 0 ? [{ label: 'Product', value: productValue }] : []),
  ];
  const brandName = client?.accountName || 'Your brand';
  const isAccepting = (frozenCollab ?? collab)?.status === 'pendingInfluencerApproval';
  const isReady = frozenCollab?.status === 'pendingRedemption';
  const storeAddress = locations.find(loc => loc.docId === selectedLocation)?.fullAddress;
  const summaryRows = [
    ...(storeAddress ? [{ k: 'Store', v: storeAddress }] : []),
    ...(selectedDate ? [{ k: deliveryOnly ? 'Deliver on' : 'Post on', v: formatPostingDate(selectedDate) }] : []),
  ];

  return (
    <Flex direction="column" minH="100svh" bg="white" fontFamily="display" color={ff.ink}>
      <Flex p="12px 12px 0 12px" align="center" gap="8px">
        <IconButton
          icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
          aria-label="Go back"
          onClick={() => navigate(-1)}
          flex="none"
          w="44px"
          h="44px"
          minW="44px"
          borderRadius="999px"
          bg="transparent"
          color={ff.ink}
          _hover={{ bg: ff.grey }}
        />
        <Text flex={1} fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A">
          {isAccepting ? 'Accepting' : 'Upcoming retail collab'}
        </Text>
      </Flex>

      <Box w="100%" maxW={{ base: '560px', lg: '720px' }} mx="auto" flex={1} display="flex" flexDirection="column">
        {/* Context card (board): brand, collab type, what the creator gets. */}
        <Flex m="14px 20px 0 20px" p="12px" borderRadius="16px" bg="#F2F2F0" align="center" gap="12px">
          <Flex flex="none" w="44px" h="44px" borderRadius="12px" bg="#ECE5D8" align="center" justify="center" overflow="hidden" fontSize="14px" fontWeight={600}>
            {client?.instagramProfilePicture ? (
              <Image src={client.instagramProfilePicture} alt="" w="100%" h="100%" objectFit="cover" />
            ) : (
              brandName.trim().slice(0, 2).toUpperCase()
            )}
          </Flex>
          <Flex flex={1} minW={0} direction="column" gap="2px">
            <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>{brandName}</Text>
            <Text fontSize="13px" color="#55555A" lineHeight="normal" noOfLines={1}>Retail{campaign?.name ? ` · ${campaign.name}` : ''}</Text>
          </Flex>
          {figures.length > 0 && (
            <Flex flex="none" gap="10px">
              {figures.map((f) => (
                <Flex key={f.label} direction="column" align="flex-end">
                  <Text textStyle="display" fontSize={`$${f.value}`.length > 5 ? '20px' : '26px'} lineHeight={1.12} textTransform="none">${f.value}</Text>
                  <Text fontSize="10px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A">{f.label}</Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <VStack spacing="28px" align="stretch" flex={1} p="24px 20px 24px 20px">
          <VStack spacing="16px" align="stretch">
            <VStack spacing="8px" align="stretch">
              <Text {...stepTitleProps}>Where will you buy it?</Text>
              <Text m={0} fontSize="15px" lineHeight="1.4" color="#55555A">
                Pick the store you'll pick up from. You buy the product there, then make your content.
              </Text>
            </VStack>
            <LocationSearchBox
              locations={locations}
              locationSearchQuery={locationSearchQuery}
              selectedLocationId={selectedLocation ?? undefined}
              onLocationSearch={setLocationSearchQuery}
              onLocationSelect={handleLocationSelect}
              isLoading={locationsLoading}
            />
          </VStack>

          <VStack spacing="16px" align="stretch">
            <Text {...stepTitleProps}>When will you {postVerb}?</Text>
            <PostingDateSelector
              scheduledPostingDate={selectedDate}
              onDateChange={handleDateChange}
              campaign={campaign}
              collab={collab}
              usePostingDateLimits={true}
            />
          </VStack>

          <Box border="1px solid #E4E4E0" borderRadius="18px" px="14px">
            <Flex
              as="button"
              type="button"
              w="100%"
              minH="52px"
              align="center"
              justify="space-between"
              aria-expanded={isInstructionsExpanded}
              onClick={() => setIsInstructionsExpanded(!isInstructionsExpanded)}
              fontSize="15px"
              fontWeight={600}
            >
              Instructions
              {isInstructionsExpanded ? <FiChevronUp size={20} aria-hidden /> : <FiChevronDown size={20} aria-hidden />}
            </Flex>
            <Collapse in={isInstructionsExpanded} animateOpacity>
              <VStack as="ol" spacing="10px" pb="14px" pl="18px" align="stretch" fontSize="14px" lineHeight="1.45" color="#3E3E42">
                <Text as="li">Accept and schedule the date you plan to {postVerb} and the location you'll pick up from</Text>
                <Text as="li">Purchase the product from your selected store location and keep your receipt. Send the receipt to the brand via the inbox and once your content is verified, your reimbursement will be available in the wallet tab.</Text>
                <Text as="li">Once you have the product, click "I am Ready to Create Content", and follow the next steps</Text>
                <Text as="li">You will be compensated after your content has been verified</Text>
              </VStack>
            </Collapse>
          </Box>

          <HelpSection
            collab={collab}
            campaign={campaign}
            client={client}
          />

          <Text fontSize="13px" lineHeight="1.45" color="#55555A">
            Make sure to check the{' '}
            <Text as="button" type="button" fontWeight={600} color={ff.ink} textDecoration="underline" onClick={() => navigate(`/collabs/collab-details/${collabId}`)}>collab details</Text>{' '}
            for any specific collab instructions and requirements
          </Text>
        </VStack>
      </Box>

      {/* Sticky CTA (board): lime pill over a hairline. */}
      <Box position="sticky" bottom={0} zIndex={10} bg="#FFFFFF" borderTop="1px solid #ECECEA" p="14px 20px" pb="calc(env(safe-area-inset-bottom) + 28px)">
        <VStack spacing="12px" maxW={{ base: '560px', lg: '720px' }} mx="auto" align="stretch">
          {(goLive.changed || goLive.blockedReason) && <PostByConfirm state={goLive} />}
          <Button
            w="100%"
            h="56px"
            borderRadius="999px"
            bg="#C6F432"
            color={ff.ink}
            fontFamily="display"
            fontSize="16px"
            fontWeight={600}
            onClick={handleMainButtonClick}
            isDisabled={!!goLive.blockedReason}
            isLoading={isRedeeming}
            _hover={{ bg: '#C6F432', _disabled: { bg: '#ECECEA' } }}
            _active={{ bg: '#AED923' }}
            _disabled={{ bg: '#ECECEA', color: '#6E6E72', cursor: 'not-allowed', opacity: 1 }}
          >
            {getButtonText()}
          </Button>
        </VStack>
      </Box>

      <WizardSheet
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setFrozenCollab(null);
        }}
        title={isReady ? 'Ready to create content?' : 'Accept and schedule?'}
      >
        <Text m={0} fontSize="15px" lineHeight="1.45" color="#3E3E42">{getConfirmationMessage()}</Text>
        {summaryRows.length > 0 && (
          <Box mt="16px" border="1px solid #E4E4E0" borderRadius="16px" px="14px">
            {summaryRows.map((r, i) => (
              <Flex key={r.k} direction="column" gap="3px" py="12px" borderBottom={i === summaryRows.length - 1 ? undefined : '1px solid #ECECEA'}>
                <Text {...eyebrowProps} lineHeight="normal">{r.k}</Text>
                <Text fontSize="15px" fontWeight={600} lineHeight="normal">{r.v}</Text>
              </Flex>
            ))}
          </Box>
        )}
        <SheetFooter>
          <SheetButton
            tone="outline"
            isDisabled={isRedeeming}
            onClick={() => {
              setIsConfirmModalOpen(false);
              setFrozenCollab(null);
            }}
          >
            Go back
          </SheetButton>
          <SheetButton onClick={handleConfirmAction} isLoading={isRedeeming}>
            {isReady ? "Yes, I'm ready" : 'Accept and Schedule'}
          </SheetButton>
        </SheetFooter>
      </WizardSheet>
    </Flex>
  );
};

export default ScheduleRetailCollabPage; 